import { can } from "@/app/auth/can";
import type { UserEntity } from "@/shared/types/user.entity";

import type { PayoutBatch } from "../types/payoutBatches.types";

/**
 * Which permission each payout batch affordance needs, taken from the `@JwtAuth` decorators of
 * `payout-batches.internal.controller.ts` (coloca-nest). Default deny, like every helper built on `can`.
 *
 * - `payout_batches:read` — list, detail, rows, trail, original file, report, template.
 * - `payout_batches:write` — upload, selection, revalidate, submit for approval, withdraw, retry preview,
 *   cancel.
 * - `payout_batches:approve:write` — execute, reject, retry, resume.
 *
 * Dual control: whoever uploaded a batch or sent it for approval never executes it, rejects it nor retries
 * its rows, whatever permission they hold (`self_approval_forbidden` in the backend). Resume re-enqueues rows
 * someone else already approved, so the maker may resume.
 */

type BatchMakers = Pick<PayoutBatch, "created_by_user_id" | "submitted_by_user_id">;

export function canWriteBatches(user: UserEntity | null | undefined): boolean {
  return can(user, "payout_batches:write");
}

export function canApproveBatches(user: UserEntity | null | undefined): boolean {
  return can(user, "payout_batches:approve:write");
}

export function isBatchUploader(
  user: UserEntity | null | undefined,
  batch: Pick<PayoutBatch, "created_by_user_id">,
): boolean {
  return !!user && user.id === batch.created_by_user_id;
}

export function isBatchSubmitter(
  user: UserEntity | null | undefined,
  batch: Pick<PayoutBatch, "submitted_by_user_id">,
): boolean {
  return !!user && !!batch.submitted_by_user_id && user.id === batch.submitted_by_user_id;
}

/** Uploaded it or sent it for approval: the maker's side of dual control. */
export function isBatchMaker(user: UserEntity | null | undefined, batch: BatchMakers): boolean {
  return isBatchUploader(user, batch) || isBatchSubmitter(user, batch);
}

/** Execute, reject and retry: the approval permission, on a batch someone else made. */
export function canApproveThisBatch(user: UserEntity | null | undefined, batch: BatchMakers): boolean {
  return canApproveBatches(user) && !isBatchMaker(user, batch);
}
